import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import server from '../../global/server';
import useHandleErrors from '../../hooks/useHandleErrors';
import Typography from '@mui/material/Typography';
import { useApp } from '../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { noAutoFormat, getResetEmail, clearResetEmail } from "../../global/helpers";

const RESEND_COOLDOWN_SECONDS = 60;

export default () => {
    const { isOffline } = useApp();
    const [hasError, setError] = useState();
    const [codeValidated, setCodeValidated] = useState(false);
    const [isResending, setResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    useHandleErrors(hasError);

    // Contagem regressiva do reenvio: o botao so volta quando chega a zero
    useEffect(() => {
        if (resendCooldown <= 0) return;

        const timer = setTimeout(() => setResendCooldown(seconds => seconds - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();

    // Pegar código da URL (se veio do link do email)
    const codeFromUrl = searchParams.get('code') || '';

    // A conta em recuperacao vem do que foi digitado em /recuperar-senha.
    // A tela nao pergunta o e-mail para nao permitir apontar o codigo para a
    // conta de outra pessoa.
    const email = searchParams.get('email') || getResetEmail();

    const ResetSchema = Yup.object().shape({
        code: Yup.string()
            .required('Código é obrigatório')
            .length(6, 'O código deve ter 6 caracteres'),
        new_password: codeValidated
            ? Yup.string()
                .required('Nova senha é obrigatória')
                .min(6, 'A senha deve ter no mínimo 6 caracteres')
            : Yup.string(),
        confirm_password: codeValidated
            ? Yup.string()
                .required('Confirmação de senha é obrigatória')
                .oneOf([Yup.ref('new_password')], 'As senhas não coincidem')
            : Yup.string(),
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = useFormik({
        initialValues: {
            code: codeFromUrl,
            new_password: '',
            confirm_password: ''
        },
        validationSchema: ResetSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                const { data } = await server.post('/auth/reset-password/validate', {
                    email,
                    code: values.code.toUpperCase(),
                    new_password: values.new_password
                });

                clearResetEmail();
                enqueueSnackbar(data.message, { variant: 'success' });
                navigate('/login');

            } catch (error) {
                console.log(error);
                setSubmitting(false);
                setError(error);
            }
        },
    });

    // Validar apenas o código (sem redefinir senha ainda)
    const handleValidateCode = async () => {
        if (!values.code || values.code.length !== 6) {
            enqueueSnackbar('Digite o código de 6 dígitos', { variant: 'warning' });
            return;
        }
        setCodeValidated(true);
    };

    // Reenvia usando a conta que ja esta em recuperacao: pedir o e-mail de
    // novo so faz sentido se essa informacao tiver se perdido.
    const handleResendCode = async () => {
        if (resendCooldown > 0) return;

        if (!email) {
            navigate('/recuperar-senha');
            return;
        }

        try {
            setResending(true);
            const { data } = await server.post('/auth/lostPass', { email });
            enqueueSnackbar(data.message, { variant: 'success' });
            // So bloqueia depois do envio confirmado: se falhou, a pessoa
            // pode tentar de novo na hora
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch (error) {
            console.log(error);
            setError(error);
        } finally {
            setResending(false);
        }
    };

    return (
        <PanelAuth>
            <div>
                <BoxAuth>
                    <form onSubmit={handleSubmit}>
                        <Grid container direction="column" gap={2}>
                            <Grid>
                                <Title>
                                    <Typography variant="h6">Redefinir senha</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {codeValidated
                                            ? 'Digite sua nova senha'
                                            : email
                                                ? <>Digite o código enviado para <strong>{email}</strong></>
                                                : 'Solicite um novo código para continuar'
                                        }
                                    </Typography>
                                </Title>
                            </Grid>

                            {!codeValidated ? (
                                <>
                                    <Grid>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            required
                                            label="Código de recuperação"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                            name="code"
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                                if (value.length <= 6) {
                                                    setFieldValue('code', value);
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            value={values.code}
                                            error={touched.code && Boolean(errors.code)}
                                            disabled={isOffline}
                                            helperText={touched.code && errors.code ? errors.code : 'Código de 6 dígitos enviado por email'}
                                            inputProps={{
                                                maxLength: 6,
                                                style: {
                                                    textAlign: 'center',
                                                    letterSpacing: '0.5rem',
                                                    fontSize: '1.2rem',
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid>
                                        <LoadingButton
                                            type="button"
                                            fullWidth
                                            onClick={handleValidateCode}
                                            variant="contained"
                                            disabled={values.code.length !== 6 || isOffline}
                                        >
                                            Validar código
                                        </LoadingButton>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            required
                                            label="Nova senha"
                                            InputLabelProps={{ shrink: true }}
                                            type="password"
                                            name="new_password"
                                            inputProps={noAutoFormat}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.new_password}
                                            error={touched.new_password && Boolean(errors.new_password)}
                                            disabled={isOffline}
                                            helperText={touched.new_password && errors.new_password}
                                        />
                                    </Grid>

                                    <Grid>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            required
                                            label="Confirmar nova senha"
                                            InputLabelProps={{ shrink: true }}
                                            type="password"
                                            name="confirm_password"
                                            inputProps={noAutoFormat}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.confirm_password}
                                            error={touched.confirm_password && Boolean(errors.confirm_password)}
                                            disabled={isOffline}
                                            helperText={touched.confirm_password && errors.confirm_password}
                                        />
                                    </Grid>

                                    <Grid>
                                        <LoadingButton
                                            type="submit"
                                            fullWidth
                                            loading={isSubmitting}
                                            variant="contained"
                                            disabled={
                                                !values.new_password ||
                                                !values.confirm_password ||
                                                values.new_password !== values.confirm_password ||
                                                isSubmitting ||
                                                isOffline
                                            }
                                        >
                                            Redefinir senha
                                        </LoadingButton>
                                    </Grid>

                                    <Grid>
                                        <LoadingButton
                                            onClick={() => setCodeValidated(false)}
                                            fullWidth
                                            variant="text"
                                            disabled={isSubmitting}
                                        >
                                            Voltar
                                        </LoadingButton>
                                    </Grid>
                                </>
                            )}

                            {!codeValidated && (
                                <Grid>
                                    <LoadingButton
                                        type="button"
                                        onClick={handleResendCode}
                                        fullWidth
                                        variant="text"
                                        loading={isResending}
                                        disabled={isResending || resendCooldown > 0 || isOffline}
                                    >
                                        {resendCooldown > 0
                                            ? `Reenviar código em ${resendCooldown}s`
                                            : 'Reenviar código'}
                                    </LoadingButton>
                                </Grid>
                            )}

                            <Grid>
                                <LoadingButton
                                    onClick={() => navigate('/login')}
                                    fullWidth
                                    variant="text"
                                >
                                    Voltar para login
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </form>
                </BoxAuth>
            </div>
        </PanelAuth>
    );
};

const PanelAuth = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #eee;
`;

const BoxAuth = styled(Grid)`
    border: 1px solid #ccc;
    border-radius: 1rem;
    padding: 2rem;
    width: 450px;
    background: white;
`;

const Title = styled.div`
    text-align: center;
    margin-bottom: 20px;
`;
